import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './SpeechToText.css';

const DeepgramSimple = ({ onTranscription, deepgramApiKey }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const startRecording = async () => {
        console.log("🎤 Starting Deepgram Simple recording...");
        audioChunksRef.current = [];

        try {
            // Get high-quality audio stream
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: true,
                    sampleRate: 16000,
                    channelCount: 1
                } 
            });

            console.log("✅ Got microphone stream");

            // Create MediaRecorder with WAV format
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus'
            });
            
            mediaRecorderRef.current = mediaRecorder;
            console.log("📹 MediaRecorder created with:", mediaRecorder.mimeType);

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    console.log("📦 Audio chunk received:", event.data.size, "bytes");
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                console.log("🛑 Recording stopped, processing...");
                setIsProcessing(true);
                
                const audioBlob = new Blob(audioChunksRef.current, { 
                    type: 'audio/webm'
                });
                
                console.log("🎵 Audio blob created:", audioBlob.size, "bytes");
                
                // Send to Deepgram
                await convertAndSendToDeepgram(audioBlob);
                
                // Clean up
                stream.getTracks().forEach(track => track.stop());
                setIsProcessing(false);
            };

            mediaRecorder.start(1000); // Collect data every second
            setIsRecording(true);
            console.log("🔴 Recording started");

        } catch (err) {
            console.error('❌ Error starting recording:', err);
        }
    };

    const stopRecording = () => {
        console.log("⏹️ Stopping recording...");
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const convertAndSendToDeepgram = async (audioBlob) => {
        try {
            console.log("🔄 Converting audio for Deepgram...");
            
            // Check if API key is available
            const actualApiKey = deepgramApiKey || 'cb27a9c690cd6906d2d3eddd830cd56a8c0cdb27';
            
            if (!actualApiKey || actualApiKey === 'undefined') {
                console.error("❌ No Deepgram API key available");
                if (onTranscription) {
                    onTranscription("Please restart the development server to load the Deepgram API key.");
                }
                return;
            }
            
            // First try direct Deepgram API call (works in some browsers/environments)
            try {
                console.log("🔄 Trying direct Deepgram API...");
                console.log("🔑 Using API key:", actualApiKey ? `${actualApiKey.substring(0, 8)}...` : 'NOT SET');
                
                // Try different header combinations for Deepgram
                const headers = {
                    'Authorization': `Token ${actualApiKey}`,
                    'Content-Type': 'audio/webm'
                };
                
                console.log("📤 Request headers:", headers);
                
                const response = await fetch('https://api.deepgram.com/v1/listen?model=nova-2&language=en&smart_format=true', {
                    method: 'POST',
                    headers: headers,
                    body: audioBlob
                });

                if (response.ok) {
                    const result = await response.json();
                    console.log("🎯 Direct Deepgram response:", result);
                    
                    let transcript = '';
                    if (result.results?.channels?.[0]?.alternatives?.[0]?.transcript) {
                        transcript = result.results.channels[0].alternatives[0].transcript;
                    } else if (result.results?.channels?.[0]?.alternatives?.[0]?.words) {
                        const alt = result.results.channels[0].alternatives[0];
                        transcript = alt.words.map(word => word.word || word.text || word).join(' ');
                    }

                    if (transcript && transcript.trim()) {
                        console.log("✅ Final transcript (direct):", transcript);
                        if (onTranscription) {
                            onTranscription(transcript);
                        }
                        return; // Success, exit function
                    }
                }
            } catch (directError) {
                console.warn('⚠️ Direct Deepgram call failed, trying Azure Function...', directError.message);
            }
            
            // Fallback to Azure Function (only in production/deployment)
            if (window.location.hostname !== 'localhost') {
                console.log("🔄 Sending audio to Azure Function...");
                
                const base64Audio = await blobToBase64(audioBlob);
                const audioData = base64Audio.split(',')[1]; // Remove data URL prefix
                
                const response = await fetch('/api/deepgram-transcribe', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        audio: audioData
                    })
                });

                if (response.ok) {
                    const result = await response.json();
                    console.log("🎯 Azure Function response:", result);
                    
                    if (result.success && result.transcript && result.transcript.trim()) {
                        console.log("✅ Final transcript (Azure Function):", result.transcript);
                        if (onTranscription) {
                            onTranscription(result.transcript);
                        }
                        return;
                    }
                }
            }
            
            // Final fallback - provide helpful message for development
            console.warn("⚠️ All Deepgram methods failed - using development fallback");
            if (onTranscription) {
                onTranscription("Deepgram not available in development. Speech transcription will work when deployed to Azure.");
            }

        } catch (err) {
            console.error('❌ All transcription methods failed:', err);
            if (onTranscription) {
                onTranscription("Speech transcription unavailable. Please type your message.");
            }
        }
    };

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };



    return (
        <>
            {isProcessing && createPortal(
                <div className="processing-overlay">
                    <div className="processing-message">
                        🔄 Processing your voice...
                    </div>
                </div>,
                document.body
            )}
            <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                className={`deepgram-mic-button ${isRecording ? 'recording' : ''} ${isProcessing ? 'connecting' : ''}`}
                title={isRecording ? "Stop Recording" : "Push to Talk"}
            >
                {isProcessing ? '🔄' : isRecording ? '🛑' : '🎤'}
            </button>
        </>
    );
};

export default DeepgramSimple;